import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Search, Save, Loader2, Zap } from "lucide-react";

type Listing = {
  id: string;
  title: string;
  teaser: string | null;
  description: string | null;
  price: number;
  revenue: number | null;
  ebitda: number | null;
  sector: string;
  status: string;
  location_state: string | null;
  location_suburb: string | null;
  listing_number: string | null;
  image_url: string | null;
  featured: boolean | null;
  requires_nda: boolean;
};

const SECTORS = [
  "Hospitality",
  "Retail",
  "Services",
  "Health & Beauty",
  "Automotive",
  "Professional Services",
  "Food & Beverage",
  "Education & Training",
  "Transport & Logistics",
  "Manufacturing",
  "Technology",
  "Other"
];

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "NT", "ACT"];

export function AdminListingEditor() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<"title" | "email">("title");
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [listings, setListings] = useState<Listing[]>([]);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [brokerEmail, setBrokerEmail] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a search term");
      return;
    }

    setSearching(true);
    setBrokerEmail(null);
    try {
      if (searchType === "email") {
        // Search by broker email using edge function
        const { data, error } = await supabase.functions.invoke("admin-search-broker-listings", {
          body: { broker_email: searchQuery.trim() }
        });

        if (error) throw error;

        if (data.listings?.length > 0) {
          // Edge function returns full listing details with service role (bypasses RLS)
          setListings(data.listings);
          setBrokerEmail(data.broker_email);
        } else {
          setListings([]);
          toast.info("No listings found for this email");
        }
      } else {
        // Search by title or listing number
        const { data, error } = await supabase
          .from("listings")
          .select("id, title, teaser, description, price, revenue, ebitda, sector, status, location_state, location_suburb, listing_number, image_url, featured, requires_nda")
          .or(`title.ilike.%${searchQuery}%,listing_number.ilike.%${searchQuery}%`)
          .order("created_at", { ascending: false })
          .limit(20);

        if (error) throw error;

        setListings(data || []);
        if (!data?.length) {
          toast.info("No listings found");
        }
      }
      setSelectedListing(null);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async () => {
    if (!selectedListing) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("listings")
        .update({
          title: selectedListing.title,
          teaser: selectedListing.teaser,
          description: selectedListing.description,
          price: selectedListing.price,
          revenue: selectedListing.revenue,
          ebitda: selectedListing.ebitda,
          sector: selectedListing.sector,
          status: selectedListing.status,
          location_state: selectedListing.location_state,
          location_suburb: selectedListing.location_suburb,
          image_url: selectedListing.image_url,
          featured: selectedListing.featured,
          requires_nda: selectedListing.requires_nda,
        })
        .eq("id", selectedListing.id);

      if (error) throw error;

      toast.success("Listing updated successfully");
      
      // Update the listing in the list
      setListings(prev => prev.map(l => l.id === selectedListing.id ? selectedListing : l));
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof Listing, value: any) => {
    if (!selectedListing) return;
    setSelectedListing({ ...selectedListing, [field]: value });
  };

  const handleMakeLive = async () => {
    if (!selectedListing) return;

    setActivating(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-activate-listing", {
        body: { listing_id: selectedListing.id }
      });

      if (error) throw error;

      toast.success(`Listing activated! Number: ${data.listing_number}`);
      
      // Update local state
      const updatedListing = { ...selectedListing, status: "live", listing_number: data.listing_number };
      setSelectedListing(updatedListing);
      setListings(prev => prev.map(l => l.id === selectedListing.id ? updatedListing : l));
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setActivating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Listings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <Select value={searchType} onValueChange={(v: "title" | "email") => setSearchType(v)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="title">Title/Number</SelectItem>
                <SelectItem value="email">Broker Email</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder={searchType === "email" ? "Enter broker email..." : "Search by title or listing number..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={searching}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
          {brokerEmail && (
            <p className="text-sm text-muted-foreground">Showing listings for: {brokerEmail}</p>
          )}
        </div>

        {/* Search Results */}
        {listings.length > 0 && !selectedListing && (
          <div className="space-y-2">
            <Label>Select a listing to edit:</Label>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {listings.map((listing) => (
                <div
                  key={listing.id}
                  className="p-3 border rounded-lg cursor-pointer hover:bg-muted transition-colors"
                  onClick={() => setSelectedListing(listing)}
                >
                  <div className="font-medium">{listing.title}</div>
                  <div className="text-sm text-muted-foreground">
                    #{listing.listing_number} • {listing.status} • ${listing.price.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Form */}
        {selectedListing && (
          <div className="space-y-4 border-t pt-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Editing: {selectedListing.title}</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedListing(null)}>
                Back to results
              </Button>
            </div>

            <div className="grid gap-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={selectedListing.title}
                  onChange={(e) => updateField("title", e.target.value)}
                />
              </div>

              <div>
                <Label>Teaser</Label>
                <Textarea
                  value={selectedListing.teaser || ""}
                  onChange={(e) => updateField("teaser", e.target.value)}
                  rows={2}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={selectedListing.description || ""}
                  onChange={(e) => updateField("description", e.target.value)}
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price ($)</Label>
                  <Input
                    type="number"
                    value={selectedListing.price}
                    onChange={(e) => updateField("price", parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label>Revenue ($)</Label>
                  <Input
                    type="number"
                    value={selectedListing.revenue || ""}
                    onChange={(e) => updateField("revenue", e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>EBITDA ($)</Label>
                  <Input
                    type="number"
                    value={selectedListing.ebitda || ""}
                    onChange={(e) => updateField("ebitda", e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={selectedListing.status} onValueChange={(v) => updateField("status", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="withdrawn">Withdrawn</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sector</Label>
                  <Select value={selectedListing.sector} onValueChange={(v) => updateField("sector", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map((sector) => (
                        <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>State</Label>
                  <Select value={selectedListing.location_state || ""} onValueChange={(v) => updateField("location_state", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATES.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Suburb</Label>
                <Input
                  value={selectedListing.location_suburb || ""}
                  onChange={(e) => updateField("location_suburb", e.target.value)}
                />
              </div>

              <div>
                <Label>Image URL</Label>
                <Input
                  value={selectedListing.image_url || ""}
                  onChange={(e) => updateField("image_url", e.target.value)}
                />
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={selectedListing.featured || false}
                    onCheckedChange={(v) => updateField("featured", v)}
                  />
                  <Label>Featured</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={selectedListing.requires_nda}
                    onCheckedChange={(v) => updateField("requires_nda", v)}
                  />
                  <Label>Requires NDA</Label>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={saving} className="flex-1">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
                {selectedListing.status === "draft" && (
                  <Button onClick={handleMakeLive} disabled={activating} variant="default" className="bg-green-600 hover:bg-green-700">
                    {activating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                    Make Live
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
